/* =========================================================================
   Esse Werks — Build Capture behavior (WO CAPTURE-SHARED, CS-1)

   The ONE capture-beat behavior module. Wires the "KEEP THIS BUILD" card
   (email / share / save) rendered by snippets/ew-build-capture.liquid, on
   ANY page that includes that snippet. Host-agnostic: if there's no
   [data-ew-build-capture] on the page, this is a no-op.

   Self-initializing + idempotent: it boots itself on DOMContentLoaded (or
   immediately if the DOM is already parsed) and guards against double-wiring
   via captureEl.__ewCaptureWired.

   CAPTURE-ONCE / HOSTED-BUILD CONTRACT (load-bearing):
     The build to capture lives in a per-element holder `captureEl.__ewBuild`.
       - Review self-init path (boot): __ewBuild is set to a get() SNAPSHOT (a
         defensive copy detached from live state) BEFORE listeners are wired —
         so computing toShareUrl/toSummary from it at fire time is immune to the
         host's post-add-to-cart ewConfigurator.clear(). Capture-once parity.
       - Host-driven path (mount): a host (e.g. Tune Builder) passes an EXPLICIT
         build; re-mount on recompute refreshes __ewBuild + share attrs.
     Listeners read __ewBuild AT FIRE TIME, so a re-mount updates them without
     re-binding.

   The Klaviyo (_learnq identify + 'Build Saved' track) and ewTrack
   ('build_saved') payloads here are byte-identical to the prior inline
   configure-review behavior — do not drift their keys/values.
   ========================================================================= */
(function () {
  'use strict';

  /* applyShare — refresh the share-bar attrs from the element's current build.
     Safe to call repeatedly (boot, init, and every mount/re-mount). */
  function applyShare(captureEl) {
    var cfg = window.ewConfigurator;
    var build = captureEl.__ewBuild || {};
    var url = (cfg && cfg.toShareUrl) ? cfg.toShareUrl(build) : '';
    var shareBar = captureEl.querySelector('[data-ew-share]');
    if (shareBar && url) {
      /* Share title — faithful to the section's prior headParts title
         ("YEAR MAKE MODEL TRIM — Esse Werks build"), built from the build's
         vehicle so behavior on Review is unchanged. */
      var bv = (build && build.vehicle) || {};
      var shareTitle = [bv.year, bv.make, bv.model, bv.trim].filter(Boolean).join(' ') + ' — Esse Werks build';
      shareBar.setAttribute('data-share-url', url);
      shareBar.setAttribute('data-share-title', shareTitle);
    }
  }

  function init(captureEl) {
    if (captureEl.__ewCaptureWired) return;       // idempotent (listener binding)
    captureEl.__ewCaptureWired = true;

    /* Set the share attrs from the build present at wire time. Listeners below
       read captureEl.__ewBuild lazily, so a later mount() re-mount refreshes
       both the share attrs (via applyShare) and what the listeners compute. */
    applyShare(captureEl);

    /* ---- Toast helper ---- text or HTML, auto-dismissing.
       Targets the top-level [data-ew-toast] element on the page. */
    var toastEl = document.querySelector('[data-ew-toast]');
    var toastTimer = null;
    function showToast(message, opts) {
      if (!toastEl) return;
      var asHtml = opts && opts.html;
      var dwell  = (opts && opts.dwell) || 3000;
      if (asHtml) { toastEl.innerHTML = message; } else { toastEl.textContent = message; }
      toastEl.classList.add('ew-toast--shown');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toastEl.classList.remove('ew-toast--shown'); }, dwell);
    }

    /* ---- Email path ---- mirrors the newsletter Klaviyo contract
       (tech-essays-article). Always wired (logged-out included); guarded on
       _learnq + ewTrack. Computes `sm`/`shareUrl` from captureEl.__ewBuild
       AT FIRE TIME (snapshot is detached, so post-ATC clear() is harmless). */
    var emailForm = captureEl.querySelector('[data-ew-build-email-form]');
    if (emailForm) {
      var emailInput  = emailForm.querySelector('[data-ew-build-email]');
      var emailSubmit = emailForm.querySelector('[data-ew-build-email-submit]');
      var emailResult = emailForm.querySelector('[data-ew-build-email-result]');
      function emailMsg(msg, ok) {
        emailResult.hidden = false;
        emailResult.textContent = msg;
        emailResult.style.color = ok ? 'var(--ew-green)' : 'var(--danger)';
      }
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = (emailInput.value || '').trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { emailMsg('Enter a valid email address.', false); emailInput.focus(); return; }
        if (!window._learnq) { emailMsg('That service is not ready yet — try again in a moment.', false); return; }
        emailSubmit.disabled = true;
        var cfg = window.ewConfigurator;
        var build = captureEl.__ewBuild || {};
        var sm = (cfg && cfg.toSummary)  ? cfg.toSummary(build)  : {};
        var shareUrl = (cfg && cfg.toShareUrl) ? cfg.toShareUrl(build) : '';
        var lv = (window.EWLivery && window.EWLivery.read)        ? window.EWLivery.read()        : null;
        var uc = (window.EWLivery && window.EWLivery.readUseCase) ? window.EWLivery.readUseCase() : null;
        window._learnq.push(['identify', { $email: email, $source: 'tune-builder', chassis: sm.chassis || null, model: sm.model || null, use_case: uc, livery: lv }]);
        window._learnq.push(['track', 'Build Saved', {
          source: 'tune-builder',
          chassis: sm.chassis || null, year: sm.year || null, model: sm.model || null,
          stage: sm.stage || null, option_count: sm.option_count || 0,
          total: (sm.total_cents || 0) / 100, total_cents: sm.total_cents || 0,
          share_url: shareUrl, livery: lv, use_case: uc
        }]);
        try { window.ewTrack && window.ewTrack('build_saved', { source: 'tune-builder', method: 'klaviyo', value: (sm.total_cents || 0) / 100, currency: 'USD' }); } catch (e) {}
        emailMsg('Sent. The link to reopen this build is on its way to your inbox.', true);
        emailInput.value = '';
        emailSubmit.disabled = false;
      });
    }

    /* ---- Save Build button visibility ---- */
    var saveBtn  = captureEl.querySelector('[data-ew-save-build]');
    var saveNote = captureEl.querySelector('[data-ew-save-build-note]');
    /* GARAGE-5: optional save-time name field — reveal in lockstep with the
       Save button (logged-in only). */
    var saveNameField = captureEl.querySelector('[data-ew-save-name-field]');
    if (typeof window.ewCustomerId === 'number') {
      saveBtn.hidden = false;
      saveNote.style.display = 'none';
      if (saveNameField) saveNameField.hidden = false;
    } else {
      saveBtn.hidden = true;
      saveNote.style.display = '';
      if (saveNameField) saveNameField.hidden = true;
    }

    /* ---- Save Build click ---- */
    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        if (!window.ewConfigurator || !window.ewConfigurator.saveToCustomer) {
          showToast('Save not available right now.');
          return;
        }
        saveBtn.disabled = true;
        var oldText = saveBtn.textContent;
        saveBtn.textContent = 'Saving…';
        /* GARAGE-5: pass the optional save-time name (blank -> unnamed). */
        var nameInput = captureEl.querySelector('[data-ew-save-build-name]');
        var nm = nameInput ? nameInput.value.trim() : '';
        window.ewConfigurator.saveToCustomer(nm, captureEl.__ewBuild)
          .then(function () {
            saveBtn.disabled = false;
            saveBtn.textContent = oldText;
            /* Phase 7.E confirmation: include a link to My Builds so the
               customer can see where the build landed. */
            showToast(
              'Build saved. <a href="/pages/my-builds" style="color:var(--ew-white);text-decoration:underline;">View in My Builds &rarr;</a>',
              { html: true, dwell: 6000 }
            );
          })
          .catch(function (err) {
            saveBtn.disabled = false;
            saveBtn.textContent = oldText;
            showToast((err && err.message) || 'Save failed. Try again.');
          });
      });
    }
  }

  /* snapshotInto — set captureEl.__ewBuild to a get() SNAPSHOT (defensive copy,
     detached from live state) so the self-init/Review path is immune to the
     host's post-add-to-cart ewConfigurator.clear(). Capture-once parity. */
  function snapshotInto(captureEl) {
    captureEl.__ewBuild = (window.ewConfigurator && window.ewConfigurator.get)
      ? window.ewConfigurator.get()
      : {};
  }

  function boot() {
    var el = document.querySelector('[data-ew-build-capture]');
    if (!el) return;                               // host-agnostic: no capture card -> no-op
    if (window.ewConfigurator) { snapshotInto(el); init(el); return; }
    /* ew-configurator.js is defer-loaded; if not ready yet, poll briefly
       (mirrors the host section's existing ready() poll: 10ms x up to 100 tries). */
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.ewConfigurator || tries > 100) {
        clearInterval(iv);
        if (document.querySelector('[data-ew-build-capture]')) { snapshotInto(el); init(el); }
      }
    }, 10);
  }

  /* mount — public host-driven entry. A host (e.g. Tune Builder) hands an
     EXPLICIT build for the capture card to host. Safe to call repeatedly:
       - first call wires listeners (which read __ewBuild live) + share attrs;
       - re-mount on recompute updates __ewBuild + refreshes share attrs WITHOUT
         re-binding listeners (they already read __ewBuild at fire time). */
  function mount(captureEl, build) {
    if (!captureEl) return;
    captureEl.__ewBuild = build || {};
    if (!captureEl.__ewCaptureWired) { init(captureEl); }  // binds listeners + applyShare
    else { applyShare(captureEl); }                         // refresh share attrs only
  }

  window.EWBuildCapture = { mount: mount };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
